import {
  ReloadOutlined,
  PoweroffOutlined,
  PlayCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { FloatButton, Tooltip, message } from "antd";

import {
  AssetResetDocument,
  AssetStopDocument,
  AssetStartDocument,
} from "../../graphql/graphql";
import { depromisify } from "../../utils/depromisify";

const ResetAssetButton = ({ assetId }: { assetId: string }) => {
  const [mutation, { loading }] = useMutation(AssetResetDocument);

  const handleReset = depromisify(async () => {
    const response = await mutation({
      variables: { assetId },
    });

    if (response.data?.assetReset === "OK") {
      await message.success("Asset has been reset successfully");
    } else {
      await message.error("Failed to reset asset. Please try again.");
    }
  });

  return (
    <Tooltip title="Reset Asset" placement="left">
      <FloatButton
        icon={loading ? <LoadingOutlined /> : <ReloadOutlined />}
        onClick={handleReset}
      />
    </Tooltip>
  );
};

const StopAssetButton = ({ assetId }: { assetId: string }) => {
  const [mutation, { loading }] = useMutation(AssetStopDocument);

  const handleStop = depromisify(async () => {
    const response = await mutation({
      variables: { assetId },
    });

    if (response.data?.assetStop === "OK") {
      await message.success("Asset has been stopped successfully");
    } else {
      await message.error("Failed to stop asset. Please try again.");
    }
  });

  return (
    <Tooltip title="Stop Asset" placement="left">
      <FloatButton
        icon={loading ? <LoadingOutlined /> : <PoweroffOutlined />}
        onClick={handleStop}
      />
    </Tooltip>
  );
};

const StartAssetButton = ({ assetId }: { assetId: string }) => {
  const [mutation, { loading }] = useMutation(AssetStartDocument);

  const handleStart = depromisify(async () => {
    const response = await mutation({
      variables: { assetId },
    });

    if (response.data?.assetStart === "OK") {
      await message.success("Asset has been started successfully");
    } else {
      await message.error("Failed to start asset. Please try again.");
    }
  });

  return (
    <Tooltip title="Start Asset" placement="left">
      <FloatButton
        icon={loading ? <LoadingOutlined /> : <PlayCircleOutlined />}
        onClick={handleStart}
      />
    </Tooltip>
  );
};

interface AssetControlActionsProps {
  assetId: string;
}

const AssetControlActions = ({ assetId }: AssetControlActionsProps) => {
  return (
    <>
      <StartAssetButton assetId={assetId} />
      <StopAssetButton assetId={assetId} />
      <ResetAssetButton assetId={assetId} />
    </>
  );
};

export default AssetControlActions;
